import React, { useEffect } from "react";
import "./App.css";
import UIRoutes from "./routes/Routes";
import GlobalStyle from "./common/GlobalStyle";
import { ThemeProvider } from "styled-components";
import { jwtDecode } from "jwt-decode";
import { useDispatch } from "react-redux";
import { setAccessToken, setUserInfo, setIsLoggedIn } from "./Redux/AuthSlice";
import { Navigate, useNavigate } from "react-router-dom";

const theme = {
  colors: {
    primary: "#1B68B3",
    secondary: "#FF5733",
    background: "#F4F4F4",
  },
};

// Local-only convenience: set REACT_APP_DEV_TOKEN in .env.local (gitignored,
// never committed) to a fresh token from a real foresyts.ai login when
// testing without going through that redirect each time. Decode any new one
// at jwt.io to confirm it hasn't expired. Never used in a production build.
const LOCAL_DEV_TOKEN =  
"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjbGllbnRJZCI6Ik1PSlIzQksiLCJjb21wYW55aWQiOiIiLCJ1c2VySWQiOiJsbXNAdHltZXBsdXNoci5jb20iLCJ1c2VyTmFtZSI6Ik93bmVyIiwidXNlclR5cGUiOiJPd25lciIsInByb2R1Y3RzIjpbeyJkZWZhdWx0IjoiaHIiLCJociI6dHJ1ZSwidXJsIjp7InBheSI6Imh0dHBzOi8vd3d3LnBheS50eW1lcGx1c2hyLmNvbS8iLCJociI6Imh0dHBzOi8vZGV2LmxlYXJuLnR5bWVwbHVzaHIuY29tLyJ9LCJwYXkiOnRydWV9XSwicGxhblR5cGUiOiIiLCJwYXJlbnRDb21wYW55SUQiOiIiLCJwYXlyb2xsY291bnRyaWVzIjpbXSwicGF5cm9sbHJvdXRpbmciOltdLCJwcmVmZXJlZHByb2R1Y3RzIjpbXSwiaWF0IjoxNzkwNjU1ODE4LCJleHAiOjE3OTA2OTkwMTh9.hGEaUcO6ZTXwGefVLzVnqr4XEPOnTf1Rr5njdRQf7RM"
// process.env.REACT_APP_DEV_TOKEN;

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLocal = process.env.NODE_ENV === "development";

  useEffect(() => {
    // Resolve the token once on mount, preferring an explicit ?token= override,
    // then whatever is already persisted, and only falling back to the
    // hardcoded dev token as a last resort. Re-deriving this from
    // window.location on every render (the previous behavior) meant that as
    // soon as navigate("/dashboard") stripped the ?token= query string, the
    // next render fell back to LOCAL_DEV_TOKEN and silently overwrote
    // whatever fresher token was in localStorage.
    const urlToken = new URLSearchParams(window.location.search).get("token");
    const existingToken = window.localStorage.getItem("token");
    const token = urlToken || existingToken || (isLocal ? LOCAL_DEV_TOKEN : null);

    if (token) {
      try {
        dispatch(setAccessToken(token));
        window.localStorage.setItem("token", token);
        const decodedToken = jwtDecode(token);
        const userId = decodedToken?.userId || "";
        const userName = decodedToken?.userName || "";
        const clientId = decodedToken?.clientId || "";
        const userType = decodedToken?.userType || "";
        window.localStorage.setItem("users", JSON.stringify(decodedToken));
        dispatch(
          setUserInfo({
            userId,
            userName,
            clientId,
            userType,
          })
        );
        dispatch(setIsLoggedIn(true));
        // Only jump to /dashboard when this token just arrived via the
        // foresyts.ai ?token= handoff - that's the one case where the
        // current URL isn't where the user actually wants to be. Resuming
        // a session on every other page load (a hard refresh on
        // /settings-account, back button, bookmarked deep link, etc.) hit
        // this same branch and forced navigate("/dashboard") regardless,
        // so refreshing any Settings tab bounced you straight back to the
        // dashboard.
        if (urlToken) {
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        dispatch(setIsLoggedIn(false));
      }
    } else {
      console.warn("No token found — user is not authenticated.");
      dispatch(setIsLoggedIn(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <div className="App">
          <UIRoutes />
        </div>
      </ThemeProvider>
    </React.Fragment>
  );
}

export default App;